import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { memo, useCallback, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { UserFragment, UserRoleType } from "gql/graphql";
import colors from "app/colors";
import {
  escapeRegExp,
  getUserFirstNameLastInitial,
  getUserFullName,
} from "modules/utils/Functions";
import {
  AdminAddUserIcon,
  ClearSearchIcon,
  CloseIcon,
  SearchIcon,
} from "modules/icons";
import useDisplayToast from "modules/hooks/use-display-toast";
import Toast from "modules/toasts/toast";
import { useAuth } from "modules/providers/auth-provider";
import { useUsers } from "modules/providers/users-provider";
import { useOverlay } from "modules/providers/overlay-provider";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import UserMention from "modules/_shared/mentions/user-mention";
import UserAvatar from "modules/_shared/user-avatar";
import { Logger } from "modules/utils/logger";
import ADD_USERS_TO_CALL_MUTATION from "modules/providers/video-call-provider/networking/add-users-to-call-mutation";

const logger = new Logger("synco.AddUsersToVideoCallDrawer");

const ADD_FAILED_MESSAGE = "Unable to add to the call. Please try again.";

interface IProps {
  callId: string;
  accessUserIdList: string[];
}

type ParticipantBrowseRow =
  | { kind: "heading"; title: string; sectionTopSpacing?: boolean }
  | { kind: "user"; user: UserFragment };

const AddUsersToVideoCallDrawer: React.FC<IProps> = ({
  callId,
  accessUserIdList,
}) => {
  const { closeDrawer } = useOverlay();
  const { displayToast } = useDisplayToast();
  const { usersById, nonEmailActiveUsers } = useUsers();
  const { me } = useAuth();
  const starredIdList = useMemo(() => me?.starredIdList ?? [], [me]);

  const onCallUserIdSet = useMemo(
    () => new Set(accessUserIdList),
    [accessUserIdList],
  );

  const [pendingUserIds, setPendingUserIds] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const [addUsersToCall, { loading }] = useMutation(ADD_USERS_TO_CALL_MUTATION);

  const pendingUsers = useMemo(
    () =>
      pendingUserIds
        .map((id) => usersById[id])
        .filter(
          (user): user is UserFragment =>
            !!user && user.active && user.roleType !== UserRoleType.Email,
        ),
    [pendingUserIds, usersById],
  );

  const searchTerms = useMemo(
    () => search.split(/\s/).filter((t) => t.length > 0),
    [search],
  );

  const starredUsers = useMemo(
    (): UserFragment[] =>
      starredIdList
        .map((id) => usersById[id])
        .filter(
          (user): user is UserFragment =>
            !!user &&
            user.active &&
            user.roleType !== UserRoleType.Email &&
            !onCallUserIdSet.has(user.id) &&
            !pendingUserIds.includes(user.id),
        ),
    [starredIdList, usersById, onCallUserIdSet, pendingUserIds],
  );

  const allOtherUsers = useMemo(
    (): UserFragment[] =>
      nonEmailActiveUsers.filter(
        (user) =>
          !onCallUserIdSet.has(user.id) &&
          !pendingUserIds.includes(user.id) &&
          !starredIdList.includes(user.id),
      ),
    [nonEmailActiveUsers, onCallUserIdSet, pendingUserIds, starredIdList],
  );

  const searchUsers = useMemo((): UserFragment[] => {
    if (searchTerms.length === 0) return [];
    return nonEmailActiveUsers.filter(
      (user) =>
        !onCallUserIdSet.has(user.id) &&
        !pendingUserIds.includes(user.id) &&
        searchTerms.some(
          (term) =>
            user.callout?.match(new RegExp(escapeRegExp(term), "i")) ||
            getUserFullName(user).match(new RegExp(escapeRegExp(term), "i")),
        ),
    );
  }, [nonEmailActiveUsers, onCallUserIdSet, pendingUserIds, searchTerms]);

  const browseListItems = useMemo((): ParticipantBrowseRow[] => {
    const items: ParticipantBrowseRow[] = [];
    if (starredUsers.length > 0) {
      items.push({ kind: "heading", title: "STARRED CONTACTS" });
      for (const user of starredUsers) {
        items.push({ kind: "user", user });
      }
    }
    if (allOtherUsers.length > 0) {
      items.push({
        kind: "heading",
        title: "ALL USERS",
        sectionTopSpacing: starredUsers.length > 0,
      });
      for (const user of allOtherUsers) {
        items.push({ kind: "user", user });
      }
    }
    return items;
  }, [allOtherUsers, starredUsers]);

  const addParticipant = useCallback((user: UserFragment) => {
    setPendingUserIds((prev) =>
      prev.includes(user.id) ? prev : [...prev, user.id],
    );
  }, []);

  const removePending = useCallback((user: UserFragment) => {
    setPendingUserIds((prev) => prev.filter((id) => id !== user.id));
  }, []);

  const renderRow = useCallback(
    (_: number, user: UserFragment) => (
      <Box
        as="button"
        type="button"
        w="full"
        textAlign="left"
        borderBottom={`1px solid ${colors.gray0}`}
        py="11px"
        cursor="pointer"
        _hover={{ bg: colors.whiteTertiary }}
        onClick={() => addParticipant(user)}
        aria-label={`Add ${getUserFullName(user)} to call`}
      >
        <HStack justify="space-between">
          <HStack>
            <UserAvatar userId={user.id} size="sm" displayOnlineIndicator />
            <UserMention id={user.id} />
          </HStack>
          <Box p={2} color={colors.blue} aria-hidden>
            <AdminAddUserIcon boxSize="20px" fill={colors.blue} />
          </Box>
        </HStack>
      </Box>
    ),
    [addParticipant],
  );

  const renderBrowseRow = useCallback(
    (_index: number, item: ParticipantBrowseRow) => {
      if (item.kind === "heading") {
        return (
          <Text
            flexShrink={0}
            color={colors.grayMain}
            fontSize={14}
            fontWeight={500}
            py={1}
            borderBottom={`1px solid ${colors.gray0}`}
            mt={item.sectionTopSpacing ? "24px" : undefined}
          >
            {item.title}
          </Text>
        );
      }
      return renderRow(0, item.user);
    },
    [renderRow],
  );

  const submit = useCallback(async () => {
    if (pendingUserIds.length === 0) return;
    const names = pendingUsers.map(getUserFirstNameLastInitial).join(", ");
    try {
      const { errors } = await addUsersToCall({
        variables: {
          input: { callId, userIdList: pendingUserIds },
        },
      });
      if (errors?.length) {
        logger.error(errors);
        displayToast(() => <Toast message={ADD_FAILED_MESSAGE} />);
        return;
      }
      displayToast(() => <Toast message={`Calling ${names}…`} />);
      closeDrawer();
    } catch (e) {
      logger.error(e);
      displayToast(() => <Toast message={ADD_FAILED_MESSAGE} />);
    }
  }, [
    addUsersToCall,
    callId,
    closeDrawer,
    displayToast,
    pendingUserIds,
    pendingUsers,
  ]);

  return (
    <DrawerContent
      display="flex"
      flexDirection="column"
      overflow="hidden"
      h="100vh"
      maxH="100vh"
    >
      <DrawerCloseButton right={0} left={1} />
      <DrawerHeader flexShrink={0}>
        <Text
          fontFamily="Outfit"
          fontSize={20}
          fontWeight={700}
          textAlign="center"
          w="full"
        >
          Add Users
        </Text>
      </DrawerHeader>
      <DrawerBody
        display="flex"
        flexDirection="column"
        flex="1"
        minH={0}
        overflow="hidden"
        p={0}
        px={6}
        pb={4}
      >
        <VStack w="full" gap={0} align="stretch" flexShrink={0} pt={3}>
          <InputGroup mb="16px">
            <InputLeftElement color={colors.gray30} mx={0}>
              <SearchIcon fill={colors.gray30} boxSize={5} />
            </InputLeftElement>
            <Input
              rounded="full"
              size="md"
              w="full"
              backgroundColor={colors.whiteTertiary}
              placeholder="Search Contacts"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              color={colors.placeholderText}
            />
            <InputRightElement>
              <Button
                variant="link"
                size="sm"
                leftIcon={<CloseIcon fill={colors.gray30} boxSize={5} />}
                onClick={() => setSearch("")}
              />
            </InputRightElement>
          </InputGroup>
          <Box
            w="full"
            overflowX="auto"
            whiteSpace="nowrap"
            mb="10px"
            pr={3}
            pb={2}
            pl={1}
          >
            <Flex gap="24px">
              {pendingUsers.map((user) => (
                <VStack key={user.id} position="relative" flexShrink={0}>
                  <UserAvatar
                    userId={user.id}
                    size="md"
                    displayOnlineIndicator
                  />
                  <Text fontSize={9} fontWeight={600} color={colors.grayMain}>
                    {getUserFirstNameLastInitial(user)}
                  </Text>
                  <CircularIconButton
                    backgroundColor={colors.white}
                    _hover={colors.white}
                    _active={colors.white}
                    icon={<ClearSearchIcon fill={colors.gray30} />}
                    minW={5}
                    h={5}
                    aria-label="Remove"
                    onClick={() => removePending(user)}
                    position="absolute"
                    top={-1}
                    right={-1}
                  />
                </VStack>
              ))}
            </Flex>
          </Box>
        </VStack>
        <Flex
          flex="1"
          minH={0}
          minW={0}
          w="full"
          flexDirection="column"
          borderBottom={`1px solid ${colors.gray0}`}
        >
          {searchTerms.length > 0 && (
            <Box flex="1" minH={0} minW={0} w="full">
              <Virtuoso
                data={searchUsers}
                style={{ height: "100%", width: "100%" }}
                itemContent={renderRow}
              />
            </Box>
          )}
          {searchTerms.length === 0 && browseListItems.length > 0 && (
            <Box flex="1" minH={0} minW={0} w="full">
              <Virtuoso<ParticipantBrowseRow>
                data={browseListItems}
                itemContent={renderBrowseRow}
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
          )}
          {searchTerms.length === 0 && browseListItems.length === 0 && (
            <Box flex="1" minH={0} minW={0} w="full" />
          )}
        </Flex>
        <Button
          flexShrink={0}
          isDisabled={loading || pendingUserIds.length === 0}
          w="full"
          maxW="333px"
          alignSelf="center"
          mt={4}
          mb={4}
          onClick={submit}
          fontSize={16}
          fontWeight={500}
        >
          Add to call
        </Button>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(AddUsersToVideoCallDrawer);
